import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useRemoveApiKey } from "@/features/auth/hooks/use-api-keys";
import { useUser } from "@/features/auth/hooks/use-user";
import { DotsThreeOutlineIcon, Plus } from "@phosphor-icons/react";
import { useState } from "react";
import { useDateFormat, useTranslation } from "@/features/utils/i18n";
import { AddApiKeyDrawer } from "./add-api-key-drawer";

export const ApiKeysTable = () => {
  const { t } = useTranslation();
  const { format } = useDateFormat();
  const [isAddDrawerOpen, setIsAddDrawerOpen] = useState(false);
  const { data: user } = useUser();
  const apiKeys = user?.api_keys?.keys || [];

  const removeApiKeyMutation = useRemoveApiKey();

  const handleRemove = async (secret: string) => {
    removeApiKeyMutation.mutate({ secret });
  };

  const formatDate = (timestamp: number) => {
    if (timestamp === 0) return t("security.apiKeys.never");
    return format(timestamp, "PPpp");
  };

  const isLoading = removeApiKeyMutation.isPending;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <p className="text-sm text-muted-foreground">
            {t("security.apiKeys.description")}
          </p>
        </div>
        <Button
          onClick={() => setIsAddDrawerOpen(true)}
          size="sm"
          className="flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          {t("security.apiKeys.addKey")}
        </Button>
      </div>

      <div className="rounded-sm border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("security.apiKeys.name")}</TableHead>
              <TableHead>{t("security.apiKeys.secret")}</TableHead>
              <TableHead>{t("security.apiKeys.created")}</TableHead>
              <TableHead>{t("security.apiKeys.lastUsed")}</TableHead>
              <TableHead className="w-[100px]">
                {t("security.apiKeys.actions")}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {apiKeys.map((apiKey) => (
              <TableRow key={apiKey.secret}>
                <TableCell className="font-medium">{apiKey.name}</TableCell>
                <TableCell>
                  <code className="text-xs bg-muted px-2 py-1 rounded">
                    {apiKey.secret_label}
                  </code>
                </TableCell>
                <TableCell>{formatDate(apiKey.created_at)}</TableCell>
                <TableCell>
                  {apiKey.last_used_at === 0 ? (
                    <Badge variant="secondary">
                      {t("security.apiKeys.never")}
                    </Badge>
                  ) : (
                    formatDate(apiKey.last_used_at)
                  )}
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        className="h-8 w-8 p-0"
                        disabled={isLoading}
                      >
                        <span className="sr-only">{t("common.openMenu")}</span>
                        <DotsThreeOutlineIcon
                          weight="fill"
                          className="h-4 w-4"
                        />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() => handleRemove(apiKey.secret)}
                        className="text-destructive"
                        disabled={isLoading}
                      >
                        {t("security.apiKeys.remove")}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
            {apiKeys.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={5}
                  className="text-center py-8 text-muted-foreground"
                >
                  {t("security.apiKeys.noKeys")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <AddApiKeyDrawer
        open={isAddDrawerOpen}
        onOpenChange={setIsAddDrawerOpen}
      />

      <p className="text-sm text-muted-foreground mt-1">
        {t("security.apiKeys.authInstruction", {
          userId: user?.id || "user_id",
        })}
      </p>
    </div>
  );
};
